import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { MailWarning, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Shows a slim banner under the topbar when the signed-in user hasn't verified their email.
 * Offers a one-click "Resend verification email" action.
 */
export default function VerificationBanner() {
    const { user, resendVerification } = useAuth();
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    if (!user || user.email_verified) return null;

    const onResend = async () => {
        setBusy(true);
        const res = await resendVerification(user.email);
        setBusy(false);
        if (res.ok) {
            setSent(true);
            toast.success(`Verification email re-sent to ${user.email}`);
        } else {
            toast.error(res.error || "Could not resend verification email");
        }
    };

    return (
        <div
            data-testid="verification-banner"
            className="flex items-center gap-3 px-5 lg:px-10 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm"
        >
            <MailWarning className="h-4 w-4 shrink-0" />
            <span className="flex-1 leading-snug">
                Please verify your email <span className="font-medium">{user.email}</span> to secure your account. Some features may be limited until you do.
            </span>
            {sent ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Email sent
                </span>
            ) : (
                <button
                    onClick={onResend}
                    disabled={busy}
                    data-testid="verification-banner-resend"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-900 underline decoration-amber-400/60 underline-offset-4 hover:decoration-amber-700 disabled:opacity-60"
                >
                    {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                    Resend verification email
                </button>
            )}
        </div>
    );
}
